import { ref, computed } from 'vue'
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useCompanyStore } from '@/stores/company'
import { useAuthStore } from '@/stores/auth'
import { generateUniqueDocTypeId } from '@/utils/documentTypeId'
import {
  DOCUMENT_TYPES,
  COMBINED_DOCUMENT_TYPES,
  isCombinedDocumentType,
  type DocumentType
} from '@/constants/documentTypes'
import { SHIPMENT_FILE_TYPE_LABELS, type ShipmentFileType } from '@/domain/entities/ShipmentFile'

const FIRESTORE_DOC_ID = 'DOCUMENT_TYPES'

export interface CustomDocumentType {
  id: string
  nameEs: string
  nameEn: string
  createdBy: string
  createdAt: unknown
}

// Estado compartido a nivel de módulo (una sola carga por compañía)
const customTypes = ref<CustomDocumentType[]>([])
const disabledTypeIds = ref<string[]>([])
const isLoading = ref(false)
const loadedCompanyId = ref<string | null>(null)

// 'other' es el tipo de respaldo al clasificar: nunca se puede ocultar.
const ALWAYS_VISIBLE_ID = 'other'

// Referencia al doc de Firestore de la compañía. Definida a nivel de módulo
// para ser reutilizada por los métodos CRUD que se añaden en la Task 3.
function docRefFor(companyId: string) {
  const db = FirebaseProvider.getInstance().getFirestore()
  return doc(db, `Companies/${companyId}/config/${FIRESTORE_DOC_ID}`)
}

export function useDocumentTypes() {
  const companyStore = useCompanyStore()
  const authStore = useAuthStore()

  // Lista fusionada: built-in en su orden, custom antes de 'other', 'other' al final.
  const documentTypes = computed<DocumentType[]>(() => {
    const custom: DocumentType[] = customTypes.value.map(c => ({
      id: c.id, nameEs: c.nameEs, nameEn: c.nameEn,
    }))
    const builtinNoOther = DOCUMENT_TYPES.filter(dt => dt.id !== 'other')
    const other = DOCUMENT_TYPES.find(dt => dt.id === 'other')
    return other ? [...builtinNoOther, ...custom, other] : [...builtinNoOther, ...custom]
  })

  const isTypeVisible = (id: string): boolean =>
    id === ALWAYS_VISIBLE_ID || !disabledTypeIds.value.includes(id)

  // Lista para los selectores de la operación: excluye los tipos ocultos por la compañía
  // y los combinados, que no son un tipo de archivo en sí (ver documentTypes.ts).
  const visibleDocumentTypes = computed<DocumentType[]>(() =>
    documentTypes.value.filter(dt => isTypeVisible(dt.id) && !isCombinedDocumentType(dt.id))
  )

  // Lista para cargar y extraer archivos: agrega los tipos combinados cuyas partes
  // están visibles para la compañía. Disponible en todas las compañías por defecto.
  // Mantiene el orden del catálogo, así el combinado queda junto a la factura comercial.
  const uploadDocumentTypes = computed<DocumentType[]>(() =>
    documentTypes.value.filter(dt => {
      if (!isTypeVisible(dt.id)) return false
      if (!isCombinedDocumentType(dt.id)) return true
      const { fileType, additionalFileTypes } = COMBINED_DOCUMENT_TYPES[dt.id]
      return [fileType, ...additionalFileTypes].every(part => isTypeVisible(part))
    })
  )

  const loadCustomTypes = async (companyId?: string, force = false): Promise<void> => {
    const cid = companyId ?? companyStore.selectedCompanyId
    if (!cid) return
    if (!force && loadedCompanyId.value === cid) return
    isLoading.value = true
    try {
      const snap = await getDoc(docRefFor(cid))
      const data = snap.exists()
        ? (snap.data() as { customTypes?: CustomDocumentType[]; disabledTypeIds?: string[] })
        : {}
      customTypes.value = data.customTypes ?? []
      disabledTypeIds.value = data.disabledTypeIds ?? []
      loadedCompanyId.value = cid
    } catch (e) {
      console.error('[useDocumentTypes] Error cargando tipos custom:', e)
      customTypes.value = []
      disabledTypeIds.value = []
    } finally {
      isLoading.value = false
    }
  }

  // Carga perezosa idempotente para la compañía activa.
  const ensureLoaded = async (): Promise<void> => {
    const cid = companyStore.selectedCompanyId
    if (cid && loadedCompanyId.value !== cid) await loadCustomTypes(cid)
  }

  const getDocumentType = (id: string): DocumentType | undefined =>
    documentTypes.value.find(dt => dt.id === id)

  // El catálogo manda; el mapa legacy solo cubre ids que ya no están en él (p. ej. 'booking').
  const getDocumentTypeName = (id: string, locale = 'es'): string => {
    const dt = documentTypes.value.find(d => d.id === id)
    if (dt) return locale === 'en' ? dt.nameEn : dt.nameEs
    const legacy = SHIPMENT_FILE_TYPE_LABELS[id as ShipmentFileType]
    if (legacy) return locale === 'en' ? legacy.en : legacy.es
    return id
  }

  // Etiqueta "ES / EN" para las pantallas que muestran ambos idiomas.
  const getDocumentTypeBilingual = (id: string): string => {
    const es = getDocumentTypeName(id, 'es')
    const en = getDocumentTypeName(id, 'en')
    return es === id && en === id ? id : `${es} / ${en}`
  }

  const persist = async (
    companyId: string,
    data: Partial<{ customTypes: CustomDocumentType[]; disabledTypeIds: string[] }>
  ) => {
    await setDoc(docRefFor(companyId), {
      ...data,
      updatedAt: Timestamp.now(),
      createdBy: authStore.user?.uid ?? '',
    }, { merge: true })
  }

  const nameExists = (nameEs: string): boolean => {
    const norm = nameEs.trim().toLowerCase()
    return documentTypes.value.some(dt => dt.nameEs.trim().toLowerCase() === norm)
  }

  const nameExistsExcept = (nameEs: string, exceptId: string): boolean => {
    const norm = nameEs.trim().toLowerCase()
    return documentTypes.value.some(dt => dt.id !== exceptId && dt.nameEs.trim().toLowerCase() === norm)
  }

  const addCustomType = async (nameEs: string, nameEn: string): Promise<CustomDocumentType> => {
    const cid = companyStore.selectedCompanyId
    if (!cid) throw new Error('No hay compañía seleccionada')
    if (!nameEs.trim() || !nameEn.trim()) throw new Error('Nombre ES y EN son requeridos')
    if (nameExists(nameEs)) throw new Error('Ya existe un tipo con ese nombre')
    const existingIds = documentTypes.value.map(dt => dt.id)
    const created: CustomDocumentType = {
      id: generateUniqueDocTypeId(nameEs, existingIds),
      nameEs: nameEs.trim(),
      nameEn: nameEn.trim(),
      createdBy: authStore.user?.uid ?? '',
      createdAt: Timestamp.now(),
    }
    const next = [...customTypes.value, created]
    await persist(cid, { customTypes: next })
    customTypes.value = next
    return created
  }

  const updateCustomType = async (id: string, nameEs: string, nameEn: string): Promise<void> => {
    const cid = companyStore.selectedCompanyId
    if (!cid) throw new Error('No hay compañía seleccionada')
    if (!nameEs.trim() || !nameEn.trim()) throw new Error('Nombre ES y EN son requeridos')
    if (nameExistsExcept(nameEs, id)) throw new Error('Ya existe un tipo con ese nombre')
    const next = customTypes.value.map(c =>
      c.id === id ? { ...c, nameEs: nameEs.trim(), nameEn: nameEn.trim() } : c
    )
    await persist(cid, { customTypes: next })
    customTypes.value = next
  }

  const deleteCustomType = async (id: string): Promise<void> => {
    const cid = companyStore.selectedCompanyId
    if (!cid) throw new Error('No hay compañía seleccionada')
    const next = customTypes.value.filter(c => c.id !== id)
    const nextDisabled = disabledTypeIds.value.filter(t => t !== id)
    await persist(cid, { customTypes: next, disabledTypeIds: nextDisabled })
    customTypes.value = next
    disabledTypeIds.value = nextDisabled
  }

  // Muestra u oculta un tipo en los selectores de la compañía. Persiste al instante.
  const setTypeVisibility = async (id: string, visible: boolean): Promise<void> => {
    const cid = companyStore.selectedCompanyId
    if (!cid) throw new Error('No hay compañía seleccionada')
    if (id === ALWAYS_VISIBLE_ID && !visible) return
    const next = visible
      ? disabledTypeIds.value.filter(t => t !== id)
      : disabledTypeIds.value.includes(id) ? disabledTypeIds.value : [...disabledTypeIds.value, id]
    await persist(cid, { disabledTypeIds: next })
    disabledTypeIds.value = next
  }

  // dispara carga perezosa sin bloquear el setup
  void ensureLoaded()

  return {
    documentTypes,
    visibleDocumentTypes,
    uploadDocumentTypes,
    customTypes,
    disabledTypeIds,
    isLoading,
    loadedCompanyId,
    loadCustomTypes,
    ensureLoaded,
    getDocumentType,
    getDocumentTypeName,
    getDocumentTypeBilingual,
    isTypeVisible,
    setTypeVisibility,
    addCustomType,
    updateCustomType,
    deleteCustomType,
  }
}
